import type {
  WorksheetMathematicsModuleType,
  WorksheetModule,
  WorksheetSize,
} from "@arbeidsark/shared"
import { useState } from "react"
import styled, { useTheme } from "styled-components"
import { OutlineButton } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import MathematicsTaskTypesSelect from "../../selects/MathematicsTaskTypesSelect"
import ArithmeticDetailConfigurationModal from "./ArithmeticDetailConfigurationModal"
import MinMaxDetailConfigurationModal from "./MinMaxDetailConfigurationModal"

interface LayoutProps {
  layout: WorksheetSize
}

const Layout = styled.div<LayoutProps>`
  padding: 1.5rem 1.25rem;

  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Label = styled.label`
  font-size: 14px;
  display: inline-block;
`

export interface MathematicsModuleConfigurationCellProps {
  pageIndex: number
  moduleIndex: number
  layout: WorksheetSize
  configuration?: WorksheetModule
  onConfigurationChanged: (configuration: WorksheetModule) => void
  onRegenerateModule: () => void
  isFirstModule?: boolean
}

const MathematicsModuleConfigurationCell: React.FC<
  MathematicsModuleConfigurationCellProps
> = ({
  pageIndex,
  moduleIndex,
  configuration,
  layout,
  onConfigurationChanged,
  onRegenerateModule,
  isFirstModule = false,
}) => {
  const [detailConfigurationIsOpen, setDetailConfigurationIsOpen] =
    useState(false)

  const theme = useTheme()

  const translations = useTranslate({
    task: "worksheets.labels.task",
    configure: "worksheets.labels.configure",
    regenerate: "worksheets.buttons.regenerate",
  })

  return (
    <Layout layout={layout}>
      <Label>
        {translations.task} {moduleIndex + 1}
      </Label>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <MathematicsTaskTypesSelect
          width="100%"
          value={
            (configuration?.type ?? null) as WorksheetMathematicsModuleType | null
          }
          onChange={(val) => {
            const newConfiguration: WorksheetModule = {
              ...(configuration || {}),
              type: val,
              size: layout,
              configuration: undefined,
            }
            onConfigurationChanged(newConfiguration)
          }}
          clearableValue={null}
          placeholderColor={theme.colors.gray10}
        />
        <span data-tour={isFirstModule ? "module-regenerate-button" : undefined}>
          <OutlineButton
            leftIcon="sync"
            leftIconProps={{ icon: "sync", color: theme.colors.primary30 }}
            size="small"
            width="100%"
            type="button"
            onClick={onRegenerateModule}
          >
            {translations.regenerate}
          </OutlineButton>
        </span>
      </div>
      {/* Render configure buttons and modals */}
      {configuration?.type === "mathematics/arithmetic" && (
        <>
          <span data-tour={isFirstModule ? "module-configure-button" : undefined}>
            <OutlineButton
              leftIcon="cog"
              leftIconProps={{ icon: "cog", color: theme.colors.primary30 }}
              size="small"
              width="100%"
              type="button"
              onClick={() => setDetailConfigurationIsOpen(true)}
            >
              {translations.configure}
            </OutlineButton>
          </span>
          {detailConfigurationIsOpen && (
            <ArithmeticDetailConfigurationModal
              isOpen={true}
              onClose={(configuration) => {
                setDetailConfigurationIsOpen(false)
                if (configuration) {
                  const newConfiguration: WorksheetModule = {
                    ...(configuration || {}),
                    type: "mathematics/arithmetic",
                    size: layout,
                    configuration,
                  }
                  onConfigurationChanged(newConfiguration)
                }
              }}
              pageIndex={pageIndex}
              moduleIndex={moduleIndex}
              configuration={configuration.configuration}
            />
          )}
        </>
      )}
      {[
        "mathematics/neighbourNumbers",
        "mathematics/numberPatterns",
        "mathematics/numberSeries",
        "mathematics/pyramid",
        "mathematics/sorting",
        "mathematics/standardAlgorithm",
      ].includes(configuration?.type ?? "") && (
        <>
          <span data-tour={isFirstModule ? "module-configure-button" : undefined}>
            <OutlineButton
              leftIcon="cog"
              leftIconProps={{ icon: "cog", color: theme.colors.primary30 }}
              size="small"
              width="100%"
              type="button"
              onClick={() => setDetailConfigurationIsOpen(true)}
            >
              {translations.configure}
            </OutlineButton>
          </span>
          {detailConfigurationIsOpen && (
            <MinMaxDetailConfigurationModal
              isOpen={true}
              onClose={(_configuration) => {
                setDetailConfigurationIsOpen(false)
                if (_configuration) {
                  const newConfiguration: WorksheetModule = {
                    ...configuration!,
                    configuration: _configuration,
                  }
                  onConfigurationChanged(newConfiguration)
                }
              }}
              pageIndex={pageIndex}
              moduleIndex={moduleIndex}
              configuration={configuration!.configuration}
            />
          )}
        </>
      )}
    </Layout>
  )
}

export const MathematicsModuleConfigurationCellLayout = Layout

export default MathematicsModuleConfigurationCell
